/* eslint-disable react/prop-types */
import React from 'react';
import moment from 'moment';
import omit from 'lodash/omit';

import tableStyles from '../../../styles/views/components/Table.module.scss';
import styles from '../../../styles/views/pages/WriterMatch/WriterMatch.module.scss';
import ForwardIcon from '../../../../assets/icon-arrow-forward-brown-filled.svg';
import FiberNewIcon from '../../../../assets/fiber_new_icon.svg';
import AvailableIcon from '../../../../assets/green-checkmark-sign.svg';
import BusyIcon from '../../../../assets/red-stop-sign.svg';
import ProactiveIcon from '../../../../assets/fire-sign.svg';
import Accredited from '../../../../assets/accredited-icon.svg';
import ShowPassIcon from '../../../../assets/show-pass-icon.svg';
import { getUserLastLoginDate } from '../../../helpers';
import { userRoleCheckers } from '../../../../../shared/helpers';
import { AvailabilityStatus } from '../../../../../shared/enums';
import sortByFullName from '../../../helpers/reactTable/sortMethods';

const columns = {
  user: {
    Header: 'Name',
    accessor: 'user',
    customStyles: {
      width: '20%',
    },
    sortType: sortByFullName,
    customFieldMapping: {
      tableAccessor: 'user',
      documentKey: 'fullName',
    },
  },
  status: {
    Header: 'Status',
    accessor: 'availabilityStatus',
    customStyles: {
      width: '10%',
    },
    customFieldMapping: {
      tableAccessor: 'availabilityStatus',
      documentKey: 'availabilityStatus',
    },
  },
  level: {
    Header: 'Level',
    accessor: 'level',
    customStyles: {
      width: '16%',
    },
    disableSortBy: true,
    customFieldMapping: {
      tableAccessor: 'level',
      documentKey: 'level',
    },
  },
  avgRating: {
    Header: 'Rating',
    accessor: 'avgRatingsMarks.general',
    customStyles: {
      width: '10%',
    },
    customFieldMapping: {
      tableAccessor: 'avgRatingsMarks.general',
      documentKey: 'avgRatingsMarks.general',
    },
  },
  hubspotProjectsCount: {
    Header: 'ActiveTotal',
    accessor: 'hubspotProjects.count',
    customStyles: {
      width: '16%',
    },
    customFieldMapping: {
      tableAccessor: 'hubspotProjects.count',
      documentKey: 'hubspotProjects.count',
    },
  },
  experience: {
    Header: 'Experience',
    accessor: 'writerProfession.experience',
    customStyles: {
      width: '10%',
    },
    tooltip: 'experience',
    customFieldMapping: {
      tableAccessor: 'writerProfession.experience',
      documentKey: 'writerProfession.experience',
    },
  },
  distanceToCustomer: {
    Header: 'Distance (miles)',
    accessor: 'distanceToCustomer',
    customStyles: {
      width: '16%',
    },
    customFieldMapping: {
      tableAccessor: 'distanceToCustomer',
      documentKey: 'distanceToCustomer',
    },
  },
  expander: {
    id: 'expander',
    Header: ' ',
    Cell: ({ row }) => (row.canExpand ? (
      <div name="expander" className={styles.arrowWrapper}>
        {row.isExpanded ? (
          <ForwardIcon name="expander" className={styles.arrowUp} />
        ) : (
          <ForwardIcon name="expander" className={styles.arrowDown} />
        )}
      </div>
    ) : null),
    disableSortBy: true,
    customStyles: {
      width: '2%',
      padding: '0 8px 0 0',
    },
  },
};

function getColumns() {
  return Object.values(columns);
}

function getAvailabilityElement(value) {
  if (value === AvailabilityStatus.AVAILABLE) {
    return (
      <span title={t('writerStatus.available')}>
        <AvailableIcon />
      </span>
    );
  } else if (value === AvailabilityStatus.BUSY) {
    return (
      <span title={t('writerStatus.busy')}>
        <BusyIcon />
      </span>
    );
  } else if (value === AvailabilityStatus.PROACTIVE) {
    return (
      <span title={t('writerStatus.proactive')}>
        <ProactiveIcon />
      </span>
    );
  }
  return null;
}

function renderCell(cell) {
  const { original: writer = {}, customRowProps = {} } = cell?.row || {};
  const isMarked = customRowProps.isMainlist && writer.isInShortlist;
  const commonStyles = [];
  if (isMarked) commonStyles.push(styles.marked);
  const wrapperExtraStyles = !writer.isExcludedForNewProject ? '' : tableStyles.disabledWriter;

  switch (cell.column.Header) {
    case columns.user.Header:
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.highlightedTableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          <span className={styles.fullName}>
            {cell.value.fullName}
            <span className={styles.iconsWrapper}>
              {writer.isRookie && <FiberNewIcon className={styles.fiberNewIcon} />}
              {writer.writerProfession?.isAccredited && <Accredited className={styles.accreditedIcon} />}
              {writer.isExcludedForNewProject && <ShowPassIcon className={tableStyles.crossedOutEyeIcon} />}
            </span>
          </span>
          <div className={tableStyles.loginDate}>
            {cell.value.lastLoggedInAt}
          </div>
        </td>
      );
    case columns.status.Header:
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.tableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          {getAvailabilityElement(cell.value)}
        </td>
      );
    case columns.level.Header: {
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.tableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          <div className={tableStyles.levelWrapper}>
            {cell.value.map(v => (
              <span key={v} className={`${styles[v.toLowerCase()]}`}>{t(`writerLevels.${v.toLowerCase()}`, { ns: i18next.Namespace.COMMON })}</span>
            ))}
          </div>
        </td>
      );
    }
    case columns.avgRating.Header:
      return (
        <td
          {...cell.getCellProps()}
          className={[...commonStyles, tableStyles.tableData, styles.avgRating, wrapperExtraStyles].join(' ')}
          name="rating"
          style={cell.column.customStyles || {}}
        >
          {cell.value?.toFixed(1) || '0.0'}
        </td>
      );
    case columns.hubspotProjectsCount.Header:
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.highlightedTableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          <span data-tip={t('activeProjectsTooltip')}>{`(${cell.row.original.stageBasedProjectsCount}) ${cell.value || 0}`}</span>
        </td>
      );
    case columns.experience.Header:
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.tableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          {cell.value}
        </td>
      );
    case columns.distanceToCustomer.Header:
      return (
        <td {...cell.getCellProps()} className={[...commonStyles, tableStyles.tableData, wrapperExtraStyles].join(' ')} style={cell.column.customStyles || {}}>
          {cell?.value?.toFixed(1)}
        </td>
      );
    default:
      return (
        <td name="expander" className={[...commonStyles, tableStyles.tableData, styles.expander, wrapperExtraStyles].join(' ')} {...cell.getCellProps()} style={cell.column.customStyles || {}}>
          {cell.render('Cell')}
        </td>
      );
  }
}

function checkIsWriterRookie(writer) {
  const now = moment();
  const _3monthsBeforeNow = moment().subtract(3, 'months');
  if (moment(writer.createdAt).isBetween(_3monthsBeforeNow, now)) return true;

  return false;
}

const SUB_ROWS = [{}];

function getMainlist(writers) {
  const mainlist = [];
  writers.forEach((item) => {
    if (item.isInMainlist) {
      mainlist.push({
        ...omit(item, ['fullName', 'lastLoggedInAt', 'isActive']),
        isRookie: checkIsWriterRookie(item),
        subRows: SUB_ROWS,
        user: {
          fullName: item.fullName,
          lastLoggedInAt: getUserLastLoginDate({ lastLoggedInAt: item.lastLoggedInAt, isActive: item.isActive }),
        },
      });
    }
  });
  return mainlist;
}

function getShortlist(writers) {
  const shortlist = [];
  writers.forEach((item) => {
    if (item.isInShortlist) {
      shortlist.push({
        ...omit(item, ['fullName', 'lastLoggedInAt', 'isActive']),
        isRookie: checkIsWriterRookie(item),
        subRows: SUB_ROWS,
        user: {
          fullName: item.fullName,
          lastLoggedInAt: getUserLastLoginDate({ lastLoggedInAt: item.lastLoggedInAt, isActive: item.isActive }),
        },
      });
    }
  });

  return shortlist.sort((a, b) => b.sequenceIndex - a.sequenceIndex);
}

function getWriterFields(writers) {
  return writers.map(({
    fullName,
    lastLoggedInAt,
    isActive,
    availabilityStatus,
    level = [],
    avgRatingsMarks = {},
    stageBasedProjectsCount,
    hubspotProjects: {
      count: hubspotProjectsCount = 0,
    } = {},
    writerProfession = {},
    distanceToCustomer,
  }) => ({
    fullName,
    lastLoggedInAt: getUserLastLoginDate({ lastLoggedInAt, isActive }),
    availabilityStatus,
    level: level.join(', '),
    averageRating: avgRatingsMarks.general ? avgRatingsMarks.general.toFixed(1) : '-',
    activeProjects: stageBasedProjectsCount,
    totalProjects: hubspotProjectsCount,
    experience: writerProfession.experience,
    distanceToCustomer: distanceToCustomer?.toFixed(1),
    editorOutlineAverage: avgRatingsMarks.editorOutline ? avgRatingsMarks.editorOutline.toFixed(1) : '-',
    editorDraftAverage: avgRatingsMarks.editorDraft ? avgRatingsMarks.editorDraft.toFixed(1) : '-',
    customerOutlineAverage: avgRatingsMarks.customerOutline ? avgRatingsMarks.customerOutline.toFixed(1) : '-',
    customerDraftAverage: avgRatingsMarks.customerDraft ? avgRatingsMarks.customerDraft.toFixed(1) : '-',
    editorialNotes: writerProfession.editorialNotes,
    keywords: writerProfession.keywords?.join(', '),
    writingStyle: writerProfession.writingStyle?.join(', '),
    comfortLevel: writerProfession.comfortLevel?.join(', '),
    professionalExperience: writerProfession.professionalExperience,
  }));
}

function getExcelData(writers, userRole) {
  if (!writers.length) {
    return {
      shortlist: [],
      'All Writers': [],
      ...(userRoleCheckers.isAdmin(userRole) && { 'Hidden writers': [] }),
    };
  }

  return {
    shortlist: getWriterFields(writers.filter(writer => writer.isInShortlist)),
    'All Writers': getWriterFields(writers.filter(writer => writer.isInMainlist && !writer.isExcludedForNewProject)),
    ...(userRoleCheckers.isAdmin(userRole) && { 'Hidden writers': getWriterFields(writers.filter(writer => writer.isExcludedForNewProject)) }),
  };
}

function getExcelColumns() {
  return [
    'Name',
    'Last logged in',
    'Status',
    'Level',
    'Average rating',
    'Active projects',
    'Total projects',
    'Years of professional experience',
    'Distance (miles)',
    'Editor average outline feedback',
    'Editor average draft feedback',
    'Customer average outline feedback',
    'Customer average draft feedback',
    'Editorial notes',
    'Keywords',
    'Writing style',
    'Comfort level',
    'Professional experience',
  ];
}

export default {
  getColumns,
  renderCell,
  getMainlist,
  getShortlist,
  columnsFixtures: columns,
  getExcelData,
  getExcelColumns,
};
